import { invalidate, useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

export const useMainCameraSyncHandler = () => {
    const syncMainCameras = usePlacementStore(useShallow((state) => state.syncMainCameras));

    const gl = useThree((state) => state.gl);
    const camera = useThree((state) => state.camera);
    const previousZoom = useRef<number | undefined>(undefined);
    const syncAnimationFrameRef = useRef<number | null>(null);

    const scheduleMainCameraSync = useCallback(() => {
        if (syncAnimationFrameRef.current !== null) {
            cancelAnimationFrame(syncAnimationFrameRef.current);
        }

        syncAnimationFrameRef.current = window.requestAnimationFrame(() => {
            syncAnimationFrameRef.current = null;
            syncMainCameras(gl.domElement);
            invalidate();
        });
    }, [gl.domElement, syncMainCameras]);

    // Sync cameras on canvas resize events
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            scheduleMainCameraSync();
        });

        resizeObserver.observe(gl.domElement);

        return () => {
            resizeObserver.disconnect();

            if (syncAnimationFrameRef.current !== null) {
                cancelAnimationFrame(syncAnimationFrameRef.current);
                syncAnimationFrameRef.current = null;
            }
        };
    }, [gl.domElement, scheduleMainCameraSync]);

    // Sync cameras on camera typeor zoom change
    useFrame(() => {
        if (previousZoom.current !== camera.zoom) {
            previousZoom.current = camera.zoom;
            scheduleMainCameraSync();
        }
    });
};
